/**
 * Customer Portal (UI) - brands list and detail views.
 */
import { useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { TemplateAssignmentModal } from '../components/templates/TemplateAssignmentModal';
import { fetchBrands, fetchCampaigns, fetchPhoneNumbers } from '../features/api/client';
import type { Brand, Campaign, PhoneNumber, ScopeType } from '../types/portal';

type AssignmentTarget = {
  scopeType: ScopeType;
  scopeId: string;
  campaignId?: string | null;
  title: string;
};

export function createBrandAssignmentTarget(brandId: string, brandName?: string | null): AssignmentTarget {
  return {
    scopeType: 'BRAND',
    scopeId: brandId,
    title: `Assign templates - ${brandName?.trim() || brandId}`
  };
}

type BrandsRouteProps = {
  accountId: string | null;
};

export function BrandsRoute({ accountId }: BrandsRouteProps) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [brands, setBrands] = useState<Brand[]>([]);

  useEffect(() => {
    if (!accountId) return;
    let cancelled = false;
    setLoading(true);
    setError(null);
    fetchBrands(accountId)
      .then((response) => {
        if (cancelled) return;
        setBrands(response.items);
      })
      .catch((requestError) => {
        if (cancelled) return;
        setError(requestError instanceof Error ? requestError.message : String(requestError));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [accountId]);

  return (
    <>
      <div className="page-pretitle">Messaging</div>
      <h2 className="page-title mb-3">Brands</h2>
      {error ? <div className="alert alert-danger">{error}</div> : null}
      {loading ? <div className="text-secondary">Loading brands...</div> : null}

      {!loading ? (
        <div className="row row-cards">
          {brands.map((brand) => (
            <div key={brand.id} className="col-sm-6 col-lg-4">
              <Link to={`/brands/${encodeURIComponent(brand.id)}`} className="card card-link cursor-pointer">
                <div className="card-body">
                  <div className="d-flex justify-content-between align-items-start gap-2">
                    <div>
                      <h3 className="card-title mb-1">{brand.name}</h3>
                      <div className="text-secondary small">{brand.id}</div>
                    </div>
                    <span className="badge bg-orange-lt text-orange">View</span>
                  </div>
                </div>
              </Link>
            </div>
          ))}
          {!brands.length ? (
            <div className="col-12">
              <div className="card">
                <div className="card-body text-secondary">No brands found for your account.</div>
              </div>
            </div>
          ) : null}
        </div>
      ) : null}
    </>
  );
}

type BrandDetailRouteProps = {
  accountId: string | null;
};

export function BrandDetailRoute({ accountId }: BrandDetailRouteProps) {
  const { brandId } = useParams<{ brandId: string }>();
  const decodedBrandId = brandId ? decodeURIComponent(brandId) : '';
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<'details' | 'campaigns' | 'numbers'>('details');
  const [brand, setBrand] = useState<Brand | null>(null);
  const [campaigns, setCampaigns] = useState<Campaign[]>([]);
  const [numbers, setNumbers] = useState<PhoneNumber[]>([]);
  const [assignmentTarget, setAssignmentTarget] = useState<AssignmentTarget | null>(null);

  useEffect(() => {
    if (!decodedBrandId || !accountId) return;
    let cancelled = false;
    setLoading(true);
    setError(null);
    Promise.all([
      fetchBrands(accountId),
      fetchCampaigns(accountId),
      fetchPhoneNumbers(accountId, { brandId: decodedBrandId, campaignId: null, phoneNumberId: null })
    ])
      .then(([brandResponse, campaignResponse, phoneResponse]) => {
        if (cancelled) return;
        const nextBrand = brandResponse.items.find((item) => item.id === decodedBrandId) ?? null;
        const brandCampaigns = campaignResponse.items.filter((item) => item.brandId === decodedBrandId);
        setBrand(nextBrand);
        setCampaigns(brandCampaigns);
        setNumbers(phoneResponse.items);
      })
      .catch((requestError) => {
        if (cancelled) return;
        setError(requestError instanceof Error ? requestError.message : String(requestError));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [accountId, decodedBrandId]);

  const detailsRows = useMemo(
    () => [
      { label: 'Brand ID', value: brand?.id ?? decodedBrandId },
      { label: 'Name', value: brand?.name ?? 'Unknown brand' },
      { label: 'Account ID', value: accountId ?? '-' },
      { label: 'Campaigns', value: String(campaigns.length) },
      { label: 'Phone numbers', value: String(numbers.length) }
    ],
    [accountId, brand?.id, brand?.name, campaigns.length, decodedBrandId, numbers.length]
  );

  return (
    <>
      <div className="page-pretitle">Messaging</div>
      <h2 className="page-title mb-3">{brand?.name ?? 'Brand detail'}</h2>
      {error ? <div className="alert alert-danger">{error}</div> : null}
      {loading ? <div className="text-secondary">Loading brand details...</div> : null}

      {!loading ? (
        <>
          <div className="mb-3">
            <Link to="/brands" className="btn btn-sm btn-outline-secondary">
              Back to brands
            </Link>
          </div>

          <ul className="nav nav-tabs mb-3">
            <li className="nav-item">
              <button
                className={`nav-link ${activeTab === 'details' ? 'active' : ''}`}
                type="button"
                onClick={() => setActiveTab('details')}
              >
                Details
              </button>
            </li>
            <li className="nav-item">
              <button
                className={`nav-link ${activeTab === 'campaigns' ? 'active' : ''}`}
                type="button"
                onClick={() => setActiveTab('campaigns')}
              >
                Campaigns
              </button>
            </li>
            <li className="nav-item">
              <button
                className={`nav-link ${activeTab === 'numbers' ? 'active' : ''}`}
                type="button"
                onClick={() => setActiveTab('numbers')}
              >
                Numbers
              </button>
            </li>
          </ul>

          {activeTab === 'details' ? (
            <div className="card">
              <div className="card-header d-flex justify-content-between align-items-center">
                <h3 className="card-title mb-0">Brand metadata</h3>
                <button
                  type="button"
                  className="btn btn-primary btn-sm"
                  onClick={() => setAssignmentTarget(createBrandAssignmentTarget(decodedBrandId, brand?.name))}
                >
                  Assign templates
                </button>
              </div>
              <div className="table-responsive">
                <table className="table table-vcenter card-table">
                  <tbody>
                    {detailsRows.map((row) => (
                      <tr key={row.label}>
                        <th className="w-25">{row.label}</th>
                        <td>{row.value}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          ) : null}

          {activeTab === 'campaigns' ? (
            <div className="card">
              <div className="card-header">
                <h3 className="card-title mb-0">Campaigns in this brand</h3>
              </div>
              <div className="table-responsive">
                <table className="table table-vcenter card-table">
                  <thead>
                    <tr>
                      <th>Name</th>
                      <th>Campaign ID</th>
                      <th className="w-1">Open</th>
                    </tr>
                  </thead>
                  <tbody>
                    {campaigns.map((campaign) => (
                      <tr key={campaign.id}>
                        <td>{campaign.name}</td>
                        <td className="text-secondary">{campaign.id}</td>
                        <td>
                          <Link className="btn btn-sm btn-outline-primary" to={`/campaigns/${encodeURIComponent(campaign.id)}`}>
                            View
                          </Link>
                        </td>
                      </tr>
                    ))}
                    {!campaigns.length ? (
                      <tr>
                        <td colSpan={3} className="text-secondary">
                          No campaigns found for this brand.
                        </td>
                      </tr>
                    ) : null}
                  </tbody>
                </table>
              </div>
            </div>
          ) : null}

          {activeTab === 'numbers' ? (
            <div className="card">
              <div className="card-header">
                <h3 className="card-title mb-0">Phone numbers in this brand</h3>
              </div>
              <div className="table-responsive">
                <table className="table table-vcenter card-table">
                  <thead>
                    <tr>
                      <th>Label</th>
                      <th>Number</th>
                      <th>Campaign</th>
                    </tr>
                  </thead>
                  <tbody>
                    {numbers.map((number) => (
                      <tr key={number.id}>
                        <td>{number.label}</td>
                        <td>{number.e164}</td>
                        <td className="text-secondary">
                          {campaigns.find((campaign) => campaign.id === number.campaignId)?.name ?? number.campaignId}
                        </td>
                      </tr>
                    ))}
                    {!numbers.length ? (
                      <tr>
                        <td colSpan={3} className="text-secondary">
                          No phone numbers found for this brand.
                        </td>
                      </tr>
                    ) : null}
                  </tbody>
                </table>
              </div>
            </div>
          ) : null}
        </>
      ) : null}

      <TemplateAssignmentModal
        isOpen={Boolean(assignmentTarget)}
        scopeType={assignmentTarget?.scopeType ?? 'ACCOUNT'}
        scopeId={assignmentTarget?.scopeId ?? accountId ?? ''}
        campaignId={assignmentTarget?.campaignId}
        title={assignmentTarget?.title ?? 'Assign templates'}
        onClose={() => setAssignmentTarget(null)}
      />
    </>
  );
}
